import { Box, Typography, Stack } from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface LocationAvailabilitySectionProps {
  address: string;
  distance?: number;
  startDate: string;
  endDate: string;
  availabilityDuration?: number;
  showDateRange?: boolean;
}

export default function LocationAvailabilitySection({
  address,
  distance,
  startDate,
  endDate,
  availabilityDuration,
  showDateRange = false,
}: Readonly<LocationAvailabilitySectionProps>) {
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      {/* Address Section */}
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
          {t('offerDetail.address')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationOn sx={{ color: 'text.primary', fontSize: 20 }} />
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {address}
            {distance ? (
              <Typography component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                {t('offerDetail.distance', { distance: distance.toFixed(1) })}
              </Typography>
            ) : null}
          </Typography>
        </Box>
      </Box>

      {/* Availability Section */}
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
          {t('offerDetail.availability')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.primary' }}>
          {showDateRange ? (
            <Typography component="span" sx={{ color: 'text.secondary' }}>
              {t('offerDetail.dateRange', {
                start: formatDate(startDate),
                end: formatDate(endDate),
              })}
            </Typography>
          ) : availabilityDuration ? (
            <>
              {availabilityDuration}{' '}
              {availabilityDuration === 1 ? t('search.results.week') : t('search.results.weeks')}{' '}
              <Typography component="span" sx={{ color: 'text.secondary' }}>
                {t('offerDetail.untilParentheses', { date: formatDate(endDate) })}
              </Typography>
            </>
          ) : (
            <Typography component="span" sx={{ color: 'text.secondary' }}>
              {t('offerDetail.until')} {formatDate(endDate)}
            </Typography>
          )}
        </Typography>
      </Box>
    </Stack>
  );
}
